import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { useAdobePageTrackEvent } from '../../../hooks/adobeHook/useAdobePageTrackEvent';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../hooks/useLagoon';
import { usePortalShowEAPfilter } from '../../../hooks/usePortalShowEAPfilter';
import { usePrimaryCare } from '../../../hooks/usePrimaryCare';
import { useSaveRecommendationPreferences } from '../../../hooks/useSaveRecommendationPreferencesStatus';
import { useSmartChoice } from '../../../hooks/useSmartChoice';
import { useSpecialtiesResult } from '../../../hooks/useSpecialties';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import { ProviderDetails } from '../../../models/ProviderDetails';
import { useAnalyticsStore } from '../../../store/useAnalyticsStore';
import { AnalyticsStore } from '../../../store/useAnalyticsStore/analyticsStore';
import { useChipStore } from '../../../store/useChipStore';
import { ChipState } from '../../../store/useChipStore/chipStore';
import { useTypeaheadStore } from '../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../store/useTypeaheadStore/typeaheadStore';
import { getDescriptionByLevel } from '../../../utils/providerDetails.utils';
import {
  getNameAttrs,
  parseProviderName,
} from '../../../utils/providerNames.utils';
import {
  getCurrentMember,
  isMemberEAPEligible,
} from '../../../utils/user.utils';
import {
  CareCategories,
  Constants,
  ProviderType,
  ReverseCoverageTypesCodes,
} from '../../Constants';
import { FeatureFlags, SECTION_TERM } from '../../ConstantsFeatureFlags';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import { ContentWrapper } from '../../ContentWrapper';
import { EAPCode } from '../../EAPCode';
import { KsAffiliatesBanner } from '../../KsAffliates/KsAffiliatesCard';
import { toShowKSBannerOnProviderDetailsAbout } from '../../KsAffliates/KsAffiliatesUtils';
import { OutOfStateBanner } from '../../OutOfStateBanner';
import { getFeatureFlag } from '../../Utils';
import {
  convertCoverageType,
  convertProviderTypeToAdobeType,
  convertSpecialtiesList,
  getIndicatorsForProviderDetailsAboutTabsImpressions,
  getSearchBlock,
} from '../../Utils/adobeTrackUtils/adobeTrackUtils';
import { ContainerForAllSectionsStyled } from '../ProviderDetailTabs.styled';
import { BiographySection } from './BiographySection';
import { CollapseDetailsSectionRoot } from './CollapseProviderDetailsSection/CollapseDetailsSectionRoot';
import { PatientReviews } from './PatientReviews/PatientReviews';
import { ReferralBannerBox } from './ReferralBannerBox';
import { SmartChoice } from './SmartChoice';

type Props = {
  providerDetails: ProviderDetails;
  tabTitle?: string;
  coverageType?: string;
  suppressReviews: boolean;
  locationKey?: string;
  preferencesData: any;
  handleTabChange: (tab: number) => void;
  chipValue?: string;
  canShowAiProviderBio?: boolean;
  providerBioContent?: string;
};

export const About = ({
  providerDetails,
  tabTitle,
  coverageType,
  suppressReviews,
  locationKey,
  preferencesData,
  handleTabChange,
  chipValue,
  canShowAiProviderBio,
  providerBioContent,
}: Props) => {
  const selectedLocation = providerDetails?.providerLocations?.find(
    (x) => x.locationKey === locationKey
  );
  const setRecommendationPreferences = useStore(
    StoreKeys.SET_RECOMMENDATION_PREFERENCES
  );
  const isSmartChoiceWithChipValue = useSmartChoice();
  const isSmartChoiceFFEnabled = useSmartChoice(true);
  const isSmartChoiceEnabled = [
    CareCategories.ALL,
    CareCategories.PRIMARY_CARE,
    CareCategories.MEDICAL,
    'PrimaryCare',
  ]?.includes(chipValue || '')
    ? isSmartChoiceFFEnabled
    : isSmartChoiceWithChipValue;

  const [searchParams] = useSearchParams();
  const isNamedSearch = searchParams.get('isNamedSearch') === 'true';
  const isSmartChoice = isSmartChoiceEnabled && !isNamedSearch;
  const [isPageTracked, setPageTracked] = useState<boolean>(false);
  const featureFlags = useLagoon(Constants.LAGOON_TABLE.FEATURE_FLAGS)();

  const [
    showTierProviderTag,
    showReferralBanner,
    eapCodeFlag,
    enableIFP,
    enableKelseySeyboldReferralBanner,
    isEnableL2CulturalCompetencies,
    isFrequentServicesProvidedEnabled,
    isProviderExperienceSectionEnabled,
    isStateLicenseSectionEnabled,
  ] = useFeatureFlag([
    FeatureFlags.FEATURE_FLAG_KEY_CHIP_MAP[
      Constants.CHIPS_CATEGORIES.PRIMARY_CARE
    ][SECTION_TERM.TIER_ONE],
    ConstantsLagoon.FEATURE_FLAGS.REFERRAL_REQUIRED_BANNER,
    ConstantsLagoon.FEATURE_FLAGS.EAP_CODE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_IFP,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_KELSEY_SEYBOLD_REFERRAL_BANNER,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_L2_CULTURAL_COMPETENCIES,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_FREQUENT_SERVICES_PROVIDED,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PROVIDER_EXPERIENCE_SECTION,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_STATE_LICENSE_SECTION,
  ]);

  const allConfigs = useLagoon('config')();
  const ksRegularProviderTins = allConfigs?.find(
    (row) => row.key === 'KELSEY_SEYBOLD_REGULAR_PROVIDERS_TIN'
  )?.value;

  const currentMember = getCurrentMember();
  const { data: pcpData } = usePrimaryCare();

  const tier =
    showTierProviderTag && selectedLocation?.isTieredProvider ? 'tier 1' : '';
  const { hasPCPServiceAvailability } = selectedLocation || {};

  const getRaceValuesForAnalytics = () => {
    const { raceLevel1, raceLevel2, raceLevel3 } =
      providerDetails?.providerRace || {};
    const race = getDescriptionByLevel(raceLevel1, raceLevel2, raceLevel3);

    const formattedRace = race
      ?.split(',')
      .map((s) => s.trim().toLowerCase())
      .join('|');
    return formattedRace;
  };

  const getEthnicityValuesForAnalytics = () => {
    const { ethnicityLevel1, ethnicityLevel2, ethnicityLevel3 } =
      providerDetails?.providerEthnicity || {};
    const ethnicity = getDescriptionByLevel(
      ethnicityLevel1,
      ethnicityLevel2,
      ethnicityLevel3
    );

    const formattedEthnicity = ethnicity
      ?.split(',')
      .map((s) => s.trim().toLowerCase())
      .join('|');
    return formattedEthnicity;
  };
  const raceAnalyticsValue = getRaceValuesForAnalytics() || '';
  const ethnicityAnalyticsValue = getEthnicityValuesForAnalytics() || '';

  let providerRole = 'specialist';
  if (hasPCPServiceAvailability) {
    providerRole += '|pcp';
  }
  if (raceAnalyticsValue) {
    providerRole += `|race`;
  }
  if (ethnicityAnalyticsValue) {
    providerRole += `|ethnicity`;
  }

  const ksBanner =
    enableKelseySeyboldReferralBanner &&
    enableIFP &&
    toShowKSBannerOnProviderDetailsAbout(
      coverageType,
      providerDetails,
      currentMember,
      ksRegularProviderTins
    );

  const { storeCoverageType } = useChipStore(
    useShallow((state: ChipState) => ({
      storeCoverageType: state.coverageType,
    }))
  );
  const { typeaheadState } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      typeaheadState: state.typeaheadSearchStore,
    }))
  );
  const { linkName } = useAnalyticsStore(
    useShallow((state: AnalyticsStore) => ({
      linkName: state.analyticsState.linkName,
    }))
  );
  const isBHCare = storeCoverageType?.includes(
    ReverseCoverageTypesCodes['BEHAVIORAL HEALTH']
  );
  const [isPreferencesModalOpen, setPreferencesModalOpen] = useState(false);

  const [savePreferencesData, saveRecommendationPreferences] =
    useSaveRecommendationPreferences({
      handleSuccess: () => {
        setPreferencesModalOpen(false);
      },
    });

  useEffect(() => {
    if (isSmartChoice && savePreferencesData?.data) {
      setRecommendationPreferences(
        savePreferencesData?.data?.saveRecommendationPreferencesStatus
      );
    }
  }, [isSmartChoice, savePreferencesData]);

  const specialties =
    convertSpecialtiesList(
      providerDetails?.defaultTranslatedValues?.specialties
    ) || '';

  const specialtiesForAnalyticsParts = [
    specialties,
    raceAnalyticsValue,
    ethnicityAnalyticsValue,
  ];

  const specialitiesForAnalytics = specialtiesForAnalyticsParts
    ?.filter(Boolean)
    .join('|');

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: tabTitle,
    sitesectionLevel1: Constants.ADOBE_TRACKING.DETAILS_SITESECTION1,
    sitesectionLevel2: `${convertProviderTypeToAdobeType(
      providerDetails.providerType
    )} details`,
    impressionBlock: {
      type: convertProviderTypeToAdobeType(providerDetails.providerType),
      indicator: getIndicatorsForProviderDetailsAboutTabsImpressions(
        selectedLocation,
        featureFlags,
        coverageType
      ),
      ...(isSmartChoice && {
        message: 'smart choice displayed',
        smartChoiceScore: `smart choice score:${
          selectedLocation?.recommendationDetails?.totalRecommendationScore || 0
        }`,
      }),
    },
    providerBlock: {
      type: convertCoverageType(coverageType),
      specialty: specialitiesForAnalytics,
      tier,
      role: providerRole || '',
    },

    searchBlock: getSearchBlock(typeaheadState, linkName),
  });

  useEffect(() => {
    if (selectedLocation) {
      adobePageTrackEvent();
    }
    setPageTracked(true);
  }, [selectedLocation !== undefined]);

  const userState = selectedLocation?.address?.state;
  const { data: referralRequiredSpecialties = [] } = useSpecialtiesResult({
    isReferralRequired: true,
    userState,
  });

  const providerSpecialties = providerDetails?.specialties;

  const result = referralRequiredSpecialties.filter((allSpecialties) =>
    providerSpecialties.find(
      (referralSpecialty) =>
        referralSpecialty?.toLocaleLowerCase() ===
        allSpecialties?.name?.toLocaleLowerCase()
    )
  );

  const isProviderSpeciality = (searchResults: string | undefined) =>
    searchResults && searchResults.length > 0;
  const searchBySpecialtyOrProvider = isProviderSpeciality(result);
  const nameAttrs = getNameAttrs(providerDetails);

  /** Suppressing EAP AOE option for non-EAP clients */
  const portalShowEAPfilter = usePortalShowEAPfilter();
  const disableEapTypeheadForNonEapClient: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_EAP_TYPEAHEAD_FOR_NON_EAP_CLIENT
  );

  const excludeEapOptionFromAoe =
    disableEapTypeheadForNonEapClient &&
    !(isMemberEAPEligible() || portalShowEAPfilter);
  const areaOfExpertise = excludeEapOptionFromAoe
    ? providerDetails?.areaOfExpertise?.filter((item) => !item.includes('EAP'))
    : providerDetails?.areaOfExpertise;

  return (
    <div style={{ overflow: 'auto hidden', marginTop: '8px' }}>
      {!!currentMember?.isReferralRequired &&
        searchBySpecialtyOrProvider &&
        showReferralBanner && (
          <ReferralBannerBox
            data-auto-testid="referral-banner-box"
            data-testid="referral-banner-box"
            pcpData={pcpData?.primaryCare}
            specialtyWithReferral={result}
          />
        )}
      {isBHCare && eapCodeFlag && isPageTracked && (
        <ContainerForAllSectionsStyled css={{ marginBottom: '8px' }}>
          <ContentWrapper>
            <EAPCode />
          </ContentWrapper>
        </ContainerForAllSectionsStyled>
      )}
      {ksBanner ? <KsAffiliatesBanner /> : null}
      {isBHCare &&
        selectedLocation?.virtualCareOffered &&
        selectedLocation?.virtualCareOffered?.length > 0 &&
        providerDetails?.providerType?.toLowerCase() ===
          ProviderType.PRACTITIONER && (
          <OutOfStateBanner selectedLocation={selectedLocation} />
        )}
      {selectedLocation?.recommendationDetails &&
        parseInt(
          selectedLocation?.recommendationDetails?.totalRecommendationScore ||
            '0'
        ) > 0 &&
        isSmartChoice && (
          <SmartChoice
            handleTabChange={handleTabChange}
            isPreferencesModalOpen={isPreferencesModalOpen}
            locationKey={locationKey ?? ''}
            preferencesData={preferencesData}
            providerDetails={providerDetails}
            recommendationDetails={selectedLocation?.recommendationDetails}
            saveRecommendationPreferences={saveRecommendationPreferences}
            setPreferencesModalOpen={setPreferencesModalOpen}
          />
        )}
      <BiographySection
        canShowAiProviderBio={canShowAiProviderBio}
        content={providerDetails?.biographicData}
        data-auto-testid="about-tab-biography-section"
        data-testid="about-tab-biography-section"
        ethnicity={providerDetails?.ethnicity}
        gender={providerDetails?.gender}
        heading="BIOGRAPHY_SECTION.HEADING"
        language={providerDetails?.languagesSpoken}
        npi={providerDetails?.npi}
        providerBioContent={providerBioContent}
        providerEthnicity={providerDetails?.providerEthnicity}
        providerRace={providerDetails?.providerRace}
        providerType={providerDetails?.providerType}
      />
      {!suppressReviews && (
        <PatientReviews
          providerDetails={providerDetails}
          selectedLocation={selectedLocation}
        />
      )}
      <CollapseDetailsSectionRoot
        areaOfExpertise={areaOfExpertise}
        culturalCompetencies={
          isEnableL2CulturalCompetencies
            ? providerDetails?.culturalCompetencyCategories
            : providerDetails?.culturalCompetencies
        }
        educationInfo={providerDetails?.educationInfo}
        frequentServices={providerDetails?.servicesProvided}
        hospitalAffiliations={providerDetails?.hospitalAffiliations}
        isFrequentServicesProvidedEnabled={isFrequentServicesProvidedEnabled}
        isProviderExperienceSectionEnabled={isProviderExperienceSectionEnabled}
        isStateLicenseSectionEnabled={isStateLicenseSectionEnabled}
        licenseNumber={providerDetails?.licenseNumber}
        licensedStates={providerDetails?.licensedStates}
        npi={providerDetails?.npi}
        providerName={parseProviderName(
          providerDetails?.providerName,
          providerDetails?.providerType,
          providerDetails?.primaryDegrees?.[0],
          nameAttrs
        )}
        providerType={providerDetails?.providerType}
        specialties={providerDetails?.specialties}
        userState={userState}
      />
    </div>
  );
};
